// 바닥 경고 데칼: 모양별(원/부채꼴/선) InstancedMesh 3개. 모양·진행·색은 TSL 프래그먼트에서 그린다.
// 주황 → 발동 MOBS.telegraphWarnToRed 이내에서 빨강, 채워지는 면이 발동 시점까지 차오른다.
// 일반 고블린의 선딜은 텔레그래프 슬롯이 아니라 state === Windup에서 원형 링 인스턴스로 그린다(진행률 = stateTick / windup).
// 혼전 가독성: 주황 단계는 면을 아주 옅게(테두리 위주), 빨강(발동 직전)만 면·테두리를 또렷하게 그린다.
// 데칼끼리 겹치면 면 채움이 누적되어 바닥을 덮으므로, 인스턴스마다 겹친 수 k로 면 농도를 1/√k로 낮춘다(aTele.w).
// 모양·크기·위치는 판정과 같아야 하므로 바꾸지 않는다(데칼=판정 공정성). 줄이는 것은 면의 진하기뿐이다.
//
// ── 텔레그래프 언어 (C2, 2026-11-04) ─────────────────────────────────────────────
// 불변식: 모양·크기·위치 = 판정(TelegraphSlot 기하 그대로). 바꾸는 것은 색·무늬·표식뿐이다. 드로우콜·InstancedMesh 수(3)는 그대로다.
// ① 지역 팔레트(TELE_REGION_STYLE, uniform으로 주입): 주황 경고 단계의 색조·외곽선 색·빗금 색·원소 표식을 지역마다 바꾼다.
//    "곧 맞는다" 빨강 단계(발동 0.5초 전, MOBS.telegraphWarnToRed)는 모든 지역 공통 진홍이다(읽기 일관성).
//    색각 이상 대비: 빨강 단계는 색 말고도 ⒜ 빗금 간격 0.75→0.5m·굵기 0.16→0.20m ⒝ 빗금이 흐름 ⒞ 면이 더 어두운 진홍(명도 하락)
//    으로 주황 단계와 구별된다(주황 단계는 정지·성김·밝음).
// ② 원소 표식: 슬롯 중심(원)·중심선 앞쪽 0.45R(부채꼴)·시작점에서 1.1m(선)에 옅은 SDF 아이콘 하나. 새 텍스처 없음(전부 프래그먼트 셰이더).
// ③ 출처 규칙(TELE_SOURCE_RULE, 인스턴스 속성 aStyle로 주입). 출처는 GameView에 아직 없어 `setSources()` 또는 view.telegraphSources(선택 필드)로 받고,
//    없으면 모두 Mob 규칙(= 이전 모양 그대로)이다.
//
//   출처(TeleSource)       | 외곽 크림 띠 | 어두운 띠 | 면 무늬(빗금 자리)                       | 읽는 법
//   -----------------------+-------------+-----------+------------------------------------------+-----------------------------
//   Mob 0 (멧돼지·엘리트)    | 0.14m       | 0.09m     | 사선 빗금(0.75m)                         | 기준 모양(이전과 같음)
//   Boss 1 (보스 패턴)       | 0.20m       | 0.10m     | 사선 빗금(0.75m)                         | 가장 굵은 띠 = 가장 큰 위협
//   Vent 2 (분출구)          | 0.10m       | 0.08m     | 점 격자(0.75m)                           | 바닥 함정: 가는 띠 + 점
//   Shaman 3 (주술사)        | 0.12m       | 0.09m     | 교차 빗금(0.75m, 두 방향)                | 시전: 격자
//   HellBrand 4 (지옥 낙인)  | 0.16m       | 0.12m     | 판정 경계를 따라 도는 등고선 띠(0.5m 간격) | 인장: 안쪽으로 겹겹이
//   (일반 고블린 선딜 고리는 슬롯이 아니라 이전 그대로: 얇은 링·무늬 없음·원소 표식 없음)
import * as THREE from 'three/webgpu';
import * as TSL from 'three/tsl';
import { MOBS, MOB_CAPACITY, MOB_STATS } from '../shared/config';
import { MobKind, MobState, TELEGRAPH_CAPACITY, TeleShape, type GameView } from '../shared/types';
import type { GameClocks } from './clock';
import { clamp01, compose, phaseOf } from './util';

type Shape = 'circle' | 'cone' | 'line';
type N = any; // eslint-disable-line @typescript-eslint/no-explicit-any
// TSL 식 조립은 타입을 느슨하게 쓴다
const { abs, atan, attribute, float, fract, fwidth, length, max, min, mix, smoothstep, sqrt, step, time, uniform, uv, vec2, vec3 } = TSL as unknown as Record<string, N>;

type RGB = readonly [number, number, number];
/** 원소 표식 번호: 0 이끼(잎) · 1 용암(불꽃) · 2 서리(눈꽃) · 3 독(포자) · 4 모래(마름모 눈금) · 5 심연(소용돌이 고리) */
export type TeleIcon = 0 | 1 | 2 | 3 | 4 | 5;
export interface TeleRegionStyle {
  /** 0..5 (r1..r6). 알 수 없는 맵(마을 등)은 r1 */
  readonly index: number;
  /** 1이면 어두운 현무암 바닥용 R2 용암 처리(비슬롯 면·테두리까지 뜨거운 팔레트) */
  readonly hot: 0 | 1;
  /** 주황 경고 단계 채움색(선형). 빨강 단계는 모든 지역이 공통 진홍이다 */
  readonly warn: RGB;
  /** 외곽선·표식 밝은 색(선형) */
  readonly rim: RGB;
  /** 빗금·무늬 색(선형): 밝은 바닥은 어둡게, 어두운 바닥은 밝게 */
  readonly hatch: RGB;
  readonly icon: TeleIcon;
}
/** 지역 r1~r6 팔레트. 테스트(telegraph.test.ts)와 docs/COMBAT-FEEL.md 표가 같은 값을 읽는다 */
export const TELE_REGION_STYLE: readonly TeleRegionStyle[] = [
  { index: 0, hot: 0, warn: [1.0, 0.34, 0.02], rim: [1.0, 0.9, 0.6], hatch: [0.1, 0.17, 0.02], icon: 0 },       // r1 이끼 숲: 호박빛 주황(이전 주황보다 한 단계 노랑)
  { index: 1, hot: 1, warn: [0.75, 0.01, 0.012], rim: [1.0, 0.88, 0.66], hatch: [1.0, 0.88, 0.66], icon: 1 }, // r2 용암: 이전 값 그대로
  { index: 2, hot: 0, warn: [1.0, 0.36, 0.2], rim: [0.8, 0.95, 1.0], hatch: [0.03, 0.14, 0.42], icon: 2 },     // r3 서리: 연어빛 + 얼음 청백 외곽 + 짙은 청 빗금
  { index: 3, hot: 0, warn: [0.78, 0.68, 0.01], rim: [0.88, 1.0, 0.65], hatch: [0.04, 0.13, 0.02], icon: 3 },  // r4 독: 독성 황록 + 짙은 녹 빗금
  { index: 4, hot: 0, warn: [1.0, 0.3, 0.0], rim: [1.0, 0.92, 0.62], hatch: [0.22, 0.05, 0.008], icon: 4 },  // r5 모래: 번트 오렌지 + 짙은 갈색 빗금
  { index: 5, hot: 0, warn: [0.72, 0.1, 0.85], rim: [0.92, 0.8, 1.0], hatch: [0.85, 0.65, 1.0], icon: 5 },    // r6 심연: 자홍 보라 + 밝은 라벤더 빗금(어두운 바닥)
];
const MAP_REGION_INDEX: Readonly<Record<string, number>> = { r1: 0, r2: 1, r3: 2, r4: 3, r5: 4, r6: 5 };
/** 맵 id → 팔레트(마을·알 수 없는 맵은 r1) */
export function teleRegionStyle(mapId: string): TeleRegionStyle { return TELE_REGION_STYLE[MAP_REGION_INDEX[mapId] ?? 0]; }

/** 면 무늬 번호: 0 사선 빗금 · 1 교차 빗금 · 2 점 격자 · 3 경계 등고선 */
export interface TeleSourceRule { readonly border: number; readonly dark: number; readonly pattern: 0 | 1 | 2 | 3 }
/** TeleSource(Mob 0 · Boss 1 · Vent 2 · Shaman 3 · HellBrand 4) → 규칙. 범위 밖은 Mob */
export const TELE_SOURCE_RULE: readonly TeleSourceRule[] = [
  { border: 0.14, dark: 0.09, pattern: 0 },
  { border: 0.2, dark: 0.1, pattern: 0 },
  { border: 0.1, dark: 0.08, pattern: 2 },
  { border: 0.12, dark: 0.09, pattern: 1 },
  { border: 0.16, dark: 0.12, pattern: 3 },
];
export const teleSourceRule = (source: number): TeleSourceRule => TELE_SOURCE_RULE[source] ?? TELE_SOURCE_RULE[0];

/** 세 ShapeMesh(와 투과 패스)가 공유하는 uniform. update()에서 지역마다 값만 바꾼다 */
interface TeleUniforms { hot: N; warn: N; rim: N; hatch: N; icon: N; occ: N }
function makeUniforms(): TeleUniforms {
  return { hot: uniform(0), warn: uniform(new THREE.Vector3(1, 0.34, 0.02)), rim: uniform(new THREE.Vector3(1, 0.9, 0.6)), hatch: uniform(new THREE.Vector3(0.1, 0.17, 0.02)), icon: uniform(0),
    /** 투과선 가림 영역: (영웅 x, 영웅 z, 카메라 전방 x, 카메라 전방 z) */
    occ: uniform(new THREE.Vector4(0, 0, 0, -1)) };
}

/** 원소 표식 SDF(단위 좌표 q, 표식 반경 1). 음수 = 안쪽. 지역마다 하나씩 */
function iconSdf(q: N, id: N): N {
  const sel = (i: number): N => float(1).sub(abs(id.sub(i)).min(1));
  const x: N = q.x, y: N = q.y;
  // 0 잎: 45도 기울인 렌즈(vesica) + 가운데 잎맥 홈
  const lx: N = x.add(y).mul(0.7071), ly: N = y.sub(x).mul(0.7071);
  const lens: N = max(length(vec2(lx, ly.sub(0.75))).sub(1.25), length(vec2(lx, ly.add(0.75))).sub(1.25));
  const leaf: N = max(lens, mix(float(-1), float(0.07).sub(abs(ly)), step(abs(lx), 0.8)));
  // 1 불꽃: 아래는 둥글고 위로 뾰족한 물방울
  const fy: N = y.add(0.35);
  const fr: N = mix(sqrt(max(float(0.4225).sub(fy.mul(fy)), 0)), float(0.65).mul(float(1).sub(smoothstep(0, 1.3, fy))), step(0, fy));
  const flame: N = max(abs(x).sub(fr), abs(y.sub(-0.03)).sub(0.97));
  // 2 눈꽃: 60도씩 도는 가는 막대 셋 + 중심 점
  const bar = (a: number): N => { const c = Math.cos(a), s = Math.sin(a); const bx: N = x.mul(c).add(y.mul(s)), by: N = y.mul(c).sub(x.mul(s)); return max(abs(bx).sub(1), abs(by).sub(0.11)); };
  const snow: N = min(min(bar(0), bar(Math.PI / 3)), min(bar(-Math.PI / 3), length(q).sub(0.22)));
  // 3 포자: 둥근 알갱이 세 개
  const spore: N = min(min(length(vec2(x, y.sub(0.5))), length(vec2(x.add(0.5), y.add(0.35)))), length(vec2(x.sub(0.5), y.add(0.35)))).sub(0.42);
  // 4 모래: 마름모 테두리 + 중심 점
  const sand: N = min(abs(abs(x).add(abs(y)).sub(0.85)).sub(0.1), length(q).sub(0.2));
  // 5 심연: 고리 + 가운데 점
  const voidIc: N = min(abs(length(q).sub(0.72)).sub(0.13), length(q).sub(0.26));
  return leaf.mul(sel(0)).add(flame.mul(sel(1))).add(snow.mul(sel(2))).add(spore.mul(sel(3))).add(sand.mul(sel(4))).add(voidIc.mul(sel(5)));
}

/** x∈[-1,1], z∈[-1,1] 단위 사각형. uv.x = (x+1)/2, uv.y = (z+1)/2 */
function unitQuad(): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1, 0, -1, 1, 0, -1, 1, 0, 1, -1, 0, 1]), 3));
  g.setAttribute('normal', new THREE.BufferAttribute(new Float32Array([0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0]), 3));
  g.setAttribute('uv', new THREE.BufferAttribute(new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]), 2));
  g.setIndex([0, 2, 1, 0, 3, 2]);
  return g;
}

/**
 * xray: 경고 경계선만 깊이 검사 없이 한 번 더 그리는 패스(보스 몸·참격 리본에 가려져도 경계가 옅게 비친다, REL-025).
 * 경고 슬롯(보스·엘리트·멧돼지·분출구·주술사)에만 쓰고 일반 고블린 선딜 고리(slot=0)는 0이다.
 */
function makeMaterial(shape: Shape, U: TeleUniforms, xray = false): THREE.MeshBasicNodeMaterial {
  const region: N = U.hot, occ: N = U.occ;
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, depthTest: !xray });
  // aTele = (진행 0..1, 빨강 0/1, 부채꼴 반각 rad 또는 선 길이/반폭 비, 겹침 농도 0..1)
  const t: N = attribute('aTele', 'vec4');
  // aStyle = (외곽 크림 띠 폭 m, 어두운 띠 폭 m, 면 무늬 번호): 출처 규칙(TELE_SOURCE_RULE)
  const sty: N = attribute('aStyle', 'vec3');
  const p = t.x;
  const red: N = t.y;
  const dens: N = t.w;
  const lx: N = uv().x.mul(2).sub(1);
  const lz: N = uv().y.mul(2).sub(1);
  /** a..b 구간 띠(가장자리 안티앨리어싱 폭 w) */
  const band = (v: N, a: N, b: N, w: N): N => smoothstep(float(a).sub(w), float(a), v).mul(float(1).sub(smoothstep(float(b), float(b).add(w), v)));
  let fill: N, lead: N, ring: N, inside: N, extra: N = float(0);
  /** 판정 경계까지 남은 월드 거리(m, 안쪽 양수). 부채꼴은 옆선까지도 잰다 */
  let sideDist: N = float(1e4);
  if (shape === 'line') {
    // 선(돌진): 폭 방향 lx, 길이 방향 uv.y(0 = 시작점). 진행은 시작점에서 끝으로 차오른다.
    const y: N = uv().y;
    const ax: N = abs(lx);
    const wx: N = max(fwidth(ax), 0.002).mul(1.5);
    const wy: N = max(fwidth(y), 0.002).mul(1.5);
    inside = float(1).sub(smoothstep(float(1).sub(wx), 1, ax));
    fill = float(1).sub(smoothstep(p.sub(wy), p, y));
    lead = band(y, p.sub(0.05), p, wy).mul(step(0.001, p));
    ring = smoothstep(0.8, float(0.8).add(wx), ax).add(smoothstep(float(0.975).sub(wy), 0.975, y)).min(1);
    // 진행 방향을 가리키는 정적 갈매기 무늬(길이 단위 = 반폭)
    const along: N = y.mul(t.z);
    extra = step(fract(along.sub(ax.mul(0.9)).mul(0.45)), 0.18).mul(0.12).mul(float(1).sub(fill));
  } else {
    const r: N = length(vec2(lx, lz));
    const w: N = max(fwidth(r), 0.002).mul(1.5);
    inside = float(1).sub(smoothstep(float(1).sub(w), 1, r));
    fill = float(1).sub(smoothstep(p.sub(w), p, r));
    lead = band(r, p.sub(0.06), p, w).mul(step(0.001, p));
    // 바깥 링 + 안쪽 가는 링(이중선). 주황은 링 띠를 가늘게(반지름의 5%), 빨강은 굵게(10%). 바깥 경계(=판정)는 같다
    const edge: N = mix(float(0.95), float(0.9), red);
    ring = smoothstep(edge.sub(w), edge, r).add(band(r, 0.8, 0.825, w).mul(mix(float(0.25), float(0.45), red)));
    if (shape === 'cone') {
      // 부채꼴: 중심각 마스크 + 양쪽 옆선(월드 폭 일정하게 r × 각 거리)
      const ang: N = abs(atan(lx, lz));
      const dEdge: N = t.z.sub(ang).mul(r);
      const wa: N = max(fwidth(dEdge), 0.002).mul(1.5);
      inside = inside.mul(smoothstep(float(0).sub(wa), 0, dEdge));
      ring = ring.add(float(1).sub(smoothstep(0.05, float(0.05).add(wa), dEdge))).min(1);
      sideDist = dEdge;
    }
  }
  // 바닥 면은 가장자리로 갈수록 진해지는 옅은 그라데이션 + 차오른 면(겹침 농도로 약화), 밝은 선단, 가장자리 링.
  // 주황: 면 매우 옅게·링 중간, 빨강: 면·선단·링 또렷하게
  const base: N = shape === 'line'
    ? mix(float(0.05), float(0.1), red)
    : mix(float(0.025), float(0.05), red).add(length(vec2(lx, lz)).pow(2).mul(mix(float(0.06), float(0.12), red)));
  const face: N = base.add(fill.mul(mix(float(0.1), float(0.3), red))).add(extra).mul(dens);
  const ringA: N = mix(mix(float(0.36), float(0.52), dens), float(0.95), red);
  const alpha: N = inside.mul(face.add(lead.mul(mix(float(0.3), float(0.6), red))).add(ring.mul(ringA)).min(0.95));
  // 선형 색 공간 값: 화면 sRGB로 주황 (1.0, 0.5, 0.1), 빨강 (0.95, 0.1, 0.13)
  const orange: N = U.warn;
  const crimson = vec3(0.89, 0.01, 0.016);
  const c: N = mix(orange, crimson, t.y);
  // 빨강 링·선단만 블룸 문턱을 살짝 넘겨 빛나게, 주황과 면은 그 아래로 둔다(1.25배까지만: 톤매핑이 색을 흰쪽으로 빼지 않게)
  m.colorNode = c.mul(float(0.85).add(ring.add(lead).min(1).mul(red).mul(0.4)));
  // R2 테두리는 월드 폭 0.12m. 반경·길이에 따라 두께가 달라지지 않고 판정 안쪽에만 그린다.
  const ext: N = attribute('aExtent', 'vec2');
  let edgeDistance: N = shape === 'line'
    ? float(1).sub(abs(lx)).mul(ext.x).min(float(1).sub(abs(lz)).mul(ext.y))
    : float(1).sub(length(vec2(lx, lz))).mul(ext.x);
  if (shape === 'cone') edgeDistance = edgeDistance.min(sideDist.mul(ext.x));
  const cream: N = float(1).sub(smoothstep(0.10, 0.12, edgeDistance));
  const hotColor: N = mix(vec3(0.65, 0.002, 0.009), vec3(1.0, 0.86, 0.63), cream);
  const hotAlpha: N = inside.mul(mix(float(0.16).add(fill.mul(0.25)).add(red.mul(0.15)).mul(dens), float(0.98), cream));
  m.colorNode = mix(m.colorNode, hotColor, region);
  m.opacityNode = mix(alpha, hotAlpha, region);

  // ── 경고 슬롯 가독성(2026-10-19, REL-025·시안 c-boss-tele) ──────────────────
  // 색만이 아니라 명암·형태로 위험/안전을 가른다: 판정 경계 안쪽 0.14m 밝은 크림 외곽선, 그 안쪽 0.09m 어두운 띠(밝은 돌·어두운 현무암·주황 용암
  // 어디서든 경계 대비), 옅은 채움 위 사선 빗금(회색조에서도 위험 면이 보인다), 시간 진행 채움은 그대로. 모양·크기·위치(=판정)는 바꾸지 않는다.
  // slot: 원은 aTele.z(1 = 경고 슬롯, 0 = 일반 고블린 선딜 고리), 부채꼴·선은 항상 슬롯.
  const slot: N = shape === 'circle' ? t.z : float(1);
  const aaE: N = max(fwidth(edgeDistance), 0.004).mul(1.2);
  // 출처 규칙: 외곽 크림 띠 폭 bw, 어두운 띠 폭 dk(기본 Mob = 0.14 / 0.09m)
  const bw: N = sty.x, dkEnd: N = sty.x.add(sty.y);
  const creamB: N = float(1).sub(smoothstep(bw.sub(aaE), bw, edgeDistance));
  const darkB: N = smoothstep(bw.sub(aaE), bw, edgeDistance).mul(float(1).sub(smoothstep(dkEnd.sub(aaE), dkEnd, edgeDistance)));
  // 월드 미터 좌표(로컬 축) 면 무늬. 주황: 0.75m 간격·굵기 0.16m·정지. 빨강: 0.5m 간격·굵기 0.20m·흐름(색각 이상에서도 단계가 읽히게)
  const wx: N = lx.mul(ext.x), wz: N = shape === 'line' ? lz.mul(ext.y) : lz.mul(ext.x);
  const inv: N = float(1).div(mix(float(0.75), float(0.5), red));
  const wcyc: N = mix(float(0.21), float(0.4), red);
  const flow: N = time.mul(0.9).mul(red);
  const bandOf = (v: N): N => { const w: N = max(fwidth(v), 0.01); return smoothstep(float(0), w, v).mul(float(1).sub(smoothstep(wcyc.sub(w), wcyc, v))); };
  const diag1: N = bandOf(fract(wx.add(wz).mul(inv).add(flow)));
  const diag2: N = bandOf(fract(wx.sub(wz).mul(inv).sub(flow)));
  const cell: N = vec2(fract(wx.mul(inv)), fract(wz.mul(inv).add(flow))).sub(0.5);
  const dotR: N = mix(float(0.22), float(0.3), red);
  const dotAa: N = max(fwidth(length(cell)), 0.01);
  const dots: N = float(1).sub(smoothstep(dotR.sub(dotAa), dotR, length(cell)));
  const contour: N = bandOf(fract(edgeDistance.mul(inv).sub(flow)));
  const psel = (i: number): N => float(1).sub(abs(sty.z.sub(i)).min(1));
  const pattern: N = diag1.mul(psel(0)).add(max(diag1, diag2).mul(psel(1))).add(dots.mul(psel(2))).add(contour.mul(psel(3))).min(1);
  const fillCol: N = mix(U.warn, vec3(0.95, 0.03, 0.02), red);
  const creamCol: N = U.rim;
  const darkCol: N = vec3(0.045, 0.012, 0.008);
  // 빗금: 채움색이 바닥과 밝기가 비슷한 회색조·색각 이상에서도 위험 면이 줄무늬로 읽힌다.
  // 밝은 바닥(R1 이끼·R3 서리·R4 독·R5 모래)은 지역색 어두운 줄, 어두운 바닥(R2 현무암·R6 심연)은 밝은 줄로 바닥과 반대 밝기를 쓴다
  const hatchCol: N = U.hatch;
  // 선(돌진·균열)의 기본(사선 빗금 규칙)은 빗금 대신 진행 방향 갈매기 무늬(extra)를 줄로 쓴다(시안 c-dodge의 돌진선). 다른 출처 규칙은 그 무늬를 그대로 쓴다
  const stripe: N = shape === 'line' ? mix(pattern, step(float(0.001), extra), psel(0)) : pattern;
  const faceS: N = mix(float(0.2), float(0.24), red).add(fill.mul(mix(float(0.14), float(0.3), red))).add(stripe.mul(mix(float(0.22), float(0.26), red))).add(lead.mul(mix(float(0.25), float(0.45), red))).mul(dens);
  // 원소 표식(옅게): 원 = 중심, 부채꼴 = 중심선 위 0.45R, 선 = 시작점에서 1.1m. 표식 반지름은 모양 크기에 비례(상한 있음)
  const iconR: N = shape === 'line' ? ext.x.mul(0.55).min(0.55) : shape === 'cone' ? ext.x.mul(0.12).min(0.8) : ext.x.mul(0.2).min(0.7);
  const ip: N = shape === 'line' ? vec2(wx, wz.add(ext.y).sub(1.1)) : shape === 'cone' ? vec2(wx, wz.sub(ext.x.mul(0.45))) : vec2(wx, wz);
  const iq: N = ip.div(iconR);
  const isd: N = iconSdf(iq, U.icon).mul(iconR);
  const iconMask: N = float(1).sub(smoothstep(max(fwidth(isd), 0.004).negate(), max(fwidth(isd), 0.004), isd)).mul(step(length(iq), 1.6));
  const iconA: N = iconMask.mul(mix(float(0.34), float(0.26), red));
  const slotColor: N = mix(mix(mix(mix(fillCol, shape === 'line' ? mix(hatchCol, creamCol, psel(0)) : hatchCol, stripe.mul(0.6)), darkCol, darkB), creamCol, creamB), creamCol, iconA.mul(float(1).sub(creamB)));
  const slotAlpha: N = inside.mul(creamB.mul(mix(float(0.82), float(0.98), red)).max(darkB.mul(0.5)).max(faceS.min(0.85)).max(iconA));
  if (xray) {
    // 외곽선만, 옅게(가려진 곳에서도 경계가 보이게). 일반 고블린 고리는 그리지 않는다.
    // 영웅 몸이 화면에서 덮는 바닥(영웅 발에서 카메라 반대쪽으로 -0.6~3.8m, 옆 0.8m 띠)에서는 끈다: 투과선이 영웅 몸 위로 그어지지 않게(2026-10-19 R2 근접 캡처)
    const o: N = occ;
    const pw: N = TSL.positionWorld as N;
    const dx: N = pw.x.sub(o.x), dz: N = pw.z.sub(o.y);
    const along: N = dx.mul(o.z).add(dz.mul(o.w));
    const side: N = abs(dx.mul(o.w).sub(dz.mul(o.z)));
    const hide: N = smoothstep(-1.0, -0.6, along).mul(float(1).sub(smoothstep(3.4, 3.9, along))).mul(float(1).sub(smoothstep(0.6, 0.95, side)));
    m.colorNode = creamCol;
    m.opacityNode = inside.mul(creamB).mul(slot).mul(mix(float(0.3), float(0.45), red)).mul(float(1).sub(hide));
    return m;
  }
  m.colorNode = mix(m.colorNode, slotColor, slot);
  m.opacityNode = mix(m.opacityNode, slotAlpha, slot);
  return m;
}

class ShapeMesh {
  readonly mesh: THREE.InstancedMesh;
  /** 경계선 투과 패스(깊이 검사 없음, 같은 기하·인스턴스 행렬 공유) */
  readonly xray: THREE.InstancedMesh;
  readonly tele: Float32Array;
  count = 0;
  private readonly extent: THREE.InstancedBufferAttribute;
  /** 인스턴스별 출처 규칙(외곽 띠, 어두운 띠, 면 무늬) */
  private readonly style: THREE.InstancedBufferAttribute;
  constructor(shape: Shape, readonly capacity: number, order: number, readonly y: number, U: TeleUniforms) {
    const geo = unitQuad();
    this.tele = new Float32Array(capacity * 4);
    const attr = new THREE.InstancedBufferAttribute(this.tele, 4);
    attr.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aTele', attr);
    this.extent = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 2), 2);
    this.extent.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aExtent', this.extent);
    this.style = new THREE.InstancedBufferAttribute(new Float32Array(capacity * 3), 3);
    this.style.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aStyle', this.style);
    this.mesh = new THREE.InstancedMesh(geo, makeMaterial(shape, U), capacity);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = order;
    this.mesh.count = 0;
    this.xray = new THREE.InstancedMesh(geo, makeMaterial(shape, U, true), capacity);
    this.xray.instanceMatrix = this.mesh.instanceMatrix;
    this.xray.frustumCulled = false;
    // 참격 리본(20)·입자보다 뒤에 그려 그 위로도 경계가 비친다
    this.xray.renderOrder = 30;
    this.xray.count = 0;
  }
  /** 추가한 인스턴스 번호를 돌려준다(겹침 농도를 나중에 쓰기 위해) */
  add(x: number, z: number, yaw: number, sx: number, sz: number, progress: number, red: boolean, half = 0, source = 0): number {
    const i = this.count++;
    this.extent.setXY(i, sx, sz);
    const rule = teleSourceRule(source);
    this.style.setXYZ(i, rule.border, rule.dark, rule.pattern);
    compose(this.mesh.instanceMatrix.array as Float32Array, i * 16, x, this.y, z, yaw, 0, sx, 1, sz);
    const o = i * 4;
    this.tele[o] = progress; this.tele[o + 1] = red ? 1 : 0; this.tele[o + 2] = half; this.tele[o + 3] = 1;
    return i;
  }
  begin(): void { this.count = 0; }
  end(): void {
    this.mesh.count = this.count;
    this.xray.count = this.count;
    this.extent.needsUpdate = true;
    this.style.needsUpdate = true;
    this.mesh.instanceMatrix.needsUpdate = true;
    (this.mesh.geometry.getAttribute('aTele') as THREE.InstancedBufferAttribute).needsUpdate = true;
  }
}

export class TelegraphVisual {
  readonly group = new THREE.Group();
  /** 원: 슬롯 24 + 몹 선딜 링 최대 MOB_CAPACITY */
  /** 세 모양이 같이 쓰는 지역 팔레트·표식·투과선 가림 uniform(update()가 맵마다 값을 바꾼다). 시험이 값을 읽는다 */
  readonly uniforms: TeleUniforms = makeUniforms();
  private readonly circle = new ShapeMesh('circle', TELEGRAPH_CAPACITY + MOB_CAPACITY, 5, 0.03, this.uniforms);
  private readonly cone = new ShapeMesh('cone', TELEGRAPH_CAPACITY, 5, 0.035, this.uniforms);
  private readonly line = new ShapeMesh('line', TELEGRAPH_CAPACITY, 5, 0.04, this.uniforms);
  /** 슬롯별 출처(TeleSource). 없으면 모두 Mob(이전 모양). 게임이 view.telegraphSources를 채우면 그것을 쓴다 */
  private sources: ArrayLike<number> | null = null;
  private lastMap = '';
  // 이번 프레임 데칼의 바닥 외접원(겹침 계산용, 미리 할당)
  private readonly cap = TELEGRAPH_CAPACITY * 3 + MOB_CAPACITY;
  private n = 0;
  private readonly bx = new Float32Array(this.cap);
  private readonly bz = new Float32Array(this.cap);
  private readonly br = new Float32Array(this.cap);
  private readonly bTele = new Array<Float32Array>(this.cap);
  private readonly bOff = new Uint16Array(this.cap);

  constructor() {
    this.group.add(this.circle.mesh, this.cone.mesh, this.line.mesh);
    this.group.add(this.circle.xray, this.cone.xray, this.line.xray);
  }

  private note(s: ShapeMesh, i: number, x: number, z: number, r: number): void {
    const k = this.n++;
    this.bx[k] = x; this.bz[k] = z; this.br[k] = r;
    this.bTele[k] = s.tele; this.bOff[k] = i * 4 + 3;
  }

  /** 겹친 데칼 수 k(자기 포함)에 따라 면 농도 1/√k(최소 0.3) */
  private density(): void {
    const n = this.n;
    for (let i = 0; i < n; i++) {
      let k = 1;
      const x = this.bx[i], z = this.bz[i], r = this.br[i];
      for (let j = 0; j < n; j++) {
        if (j === i) continue;
        const dx = this.bx[j] - x, dz = this.bz[j] - z, rr = (this.br[j] + r) * 0.8;
        if (dx * dx + dz * dz < rr * rr) k++;
      }
      this.bTele[i][this.bOff[i]] = k === 1 ? 1 : Math.max(0.3, 1 / Math.sqrt(k));
    }
  }

  /** 렌더 프레임마다: 영웅 렌더 위치와 카메라 전방(XZ 정규화). 투과선이 영웅 몸을 덮지 않게 한다 */
  setOccluder(hx: number, hz: number, fx: number, fz: number): void {
    const l = Math.hypot(fx, fz) || 1;
    (this.uniforms.occ.value as THREE.Vector4).set(hx, hz, fx / l, fz / l);
  }

  /** 슬롯 번호별 출처(TeleSource) 배열을 받는다. null이면 모두 Mob 규칙 */
  setSources(src: ArrayLike<number> | null): void { this.sources = src; }

  /** 맵이 바뀔 때만 팔레트 uniform을 쓴다(프레임마다 쓰지 않는다) */
  private applyRegion(mapId: string): void {
    if (mapId === this.lastMap) return;
    this.lastMap = mapId;
    const st = teleRegionStyle(mapId), u = this.uniforms;
    u.hot.value = st.hot; u.icon.value = st.icon;
    (u.warn.value as THREE.Vector3).set(st.warn[0], st.warn[1], st.warn[2]);
    (u.rim.value as THREE.Vector3).set(st.rim[0], st.rim[1], st.rim[2]);
    (u.hatch.value as THREE.Vector3).set(st.hatch[0], st.hatch[1], st.hatch[2]);
  }

  update(view: GameView, alpha: number, clocks: GameClocks): void {
    this.applyRegion(view.map.id);
    const srcs = this.sources ?? view.telegraphSources ?? null;
    this.circle.begin(); this.cone.begin(); this.line.begin();
    this.n = 0;
    const rate = clocks.worldRate;
    const tel = view.telegraphs;
    for (let i = 0; i < tel.length; i++) {
      const t = tel[i];
      if (!t.active) continue;
      const p = clamp01(phaseOf(t.tick, rate, alpha) / Math.max(1, t.total));
      const red = t.total - t.tick <= MOBS.telegraphWarnToRed;
      const src = srcs ? srcs[i] ?? 0 : 0;
      // 원 슬롯은 aTele.z = 1(경고 슬롯 표시: 크림 외곽선·빗금·투과선). 일반 고블린 선딜 고리는 0
      if (t.shape === TeleShape.Circle) this.note(this.circle, this.circle.add(t.x, t.z, 0, t.radius, t.radius, p, red, 1, src), t.x, t.z, t.radius);
      else if (t.shape === TeleShape.Cone) this.note(this.cone, this.cone.add(t.x, t.z, t.angle, t.radius, t.radius, p, red, t.width / 2, src), t.x, t.z, t.radius);
      else {
        // 선: 시작점에서 angle 방향으로 길이 radius. 단위 사각형 중심을 길이의 절반만큼 이동한다.
        const dx = Math.sin(t.angle), dz = Math.cos(t.angle);
        const cx = t.x + dx * t.radius * 0.5, cz = t.z + dz * t.radius * 0.5;
        this.note(this.line, this.line.add(cx, cz, t.angle, t.width / 2, t.radius / 2, p, red, t.radius / Math.max(0.1, t.width / 2), src), cx, cz, t.radius / 2);
      }
    }
    // 일반(비엘리트) 고블린류 선딜: 슬롯 없이 몹 상태에서 직접 그린다. 멧돼지·엘리트는 게임이 슬롯을 쓴다.
    const m = view.mobs;
    for (let i = 0; i < m.capacity; i++) {
      if (m.state[i] !== MobState.Windup) continue;
      const kind = m.kind[i];
      if (kind === MobKind.Boar || kind === MobKind.Shaman || m.elite[i] !== 0) continue;
      const stats = MOB_STATS[kind];
      const total = Math.max(1, stats.windup);
      const tick = m.stateTick[i];
      const p = clamp01(phaseOf(tick, rate, alpha) / total);
      const r = stats.attackRange;
      this.note(this.circle, this.circle.add(m.x[i], m.z[i], 0, r, r, p, total - tick <= MOBS.telegraphWarnToRed), m.x[i], m.z[i], r);
    }
    this.density();
    this.circle.end(); this.cone.end(); this.line.end();
  }

  dispose(): void {
    for (const s of [this.circle, this.cone, this.line]) {
      s.mesh.geometry.dispose();
      (s.mesh.material as THREE.Material).dispose();
      (s.xray.material as THREE.Material).dispose();
      s.mesh.dispose();
    }
  }
}
